import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BarChart3, RefreshCw } from 'lucide-react';
import { performanceMonitor } from '@/services/PerformanceMonitorService';

/**
 * Phase 3: Performance monitoring widget for admins
 * Shows real-time performance metrics
 */
export const PerformanceWidget: React.FC = () => {
  const [summary, setSummary] = useState(performanceMonitor.getPerformanceSummary());
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Update every 5 seconds
    const interval = setInterval(() => {
      setSummary(performanceMonitor.getPerformanceSummary());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  if (!isVisible) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 z-50"
      >
        <BarChart3 className="h-4 w-4 mr-2" />
        Performance
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 z-50 p-4 w-80 shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          <span className="font-semibold text-sm">Performance Metrics</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSummary(performanceMonitor.getPerformanceSummary());
              performanceMonitor.logReport();
            }}
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(false)}
          >
            ×
          </Button>
        </div>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Module Load:</span>
          <Badge variant={summary.avgModuleLoad < 100 ? 'default' : 'destructive'}>
            {summary.avgModuleLoad}ms
          </Badge>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">Audio Load:</span>
          <Badge variant={summary.avgAudioLoad < 2000 ? 'default' : 'destructive'}>
            {summary.avgAudioLoad}ms
          </Badge>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">Navigation:</span>
          <Badge variant={summary.avgNavigation < 200 ? 'default' : 'destructive'}>
            {summary.avgNavigation}ms
          </Badge>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">Cache Hit Rate:</span>
          <Badge variant={summary.cacheHitRate > 90 ? 'default' : 'destructive'}>
            {summary.cacheHitRate}%
          </Badge>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">Total Metrics:</span>
          <span className="font-mono">{summary.totalMetrics}</span>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
        🎯 Target: &lt;100ms modules, &lt;2s audio, &gt;90% cache hits
      </div>
    </Card>
  );
};
